/**
 * When the meeting says "Reconnecting…", and why.
 *
 * Measured 2026-10-13 with tools/meeting_driver.py, cutting the laptop's
 * Wi-Fi: for as long as a minute and a half after the network went, the
 * meeting looked exactly like a working one — tiles frozen on their last
 * frame, no word anywhere — while signaling and media rebuilt behind it. The
 * only party ever told was an embedding host page (useEmbedConnectionEvents).
 * A global "Connection Lost" modal does open on the browser's `offline` event,
 * but it closes on `online`, long before the meeting is back.
 *
 * Kept pure — no React, no clock of its own — so the rules can be tested
 * directly; `useReconnectBanner` wires them to the meeting's state.
 */

export type ConnectionInputs = {
  /** `navigator.onLine`. False is reliable; true only means some interface is up. */
  online: boolean
  /** The room channel is joined — not merely the socket open. */
  channelJoined: boolean
  /** The SFU session (device and both transports) is up. */
  sfuReady: boolean
  /** The SFU session failed and is retrying (useSfuJoin's `error`). */
  sfuError: boolean
  /**
   * Another tab or window of this person's took the media session over, and
   * this one has stopped (useSfuJoin's error is SFU_TAKEN_OVER_ERROR).
   */
  sfuTakenOver?: boolean
}

/**
 * useSfuJoin's error once another tab or window has taken this peer's media
 * session over. Not a failure being retried, so the banner says something
 * else for it.
 */
export const SFU_TAKEN_OVER_ERROR = 'media session taken over by another tab or device'

export type ReconnectReason =
  /** The browser has no network at all. */
  | 'offline'
  /**
   * The meeting's signaling channel is down. Media is kept through it (see
   * media-session-hold), but chat, the roster and host controls are not.
   */
  | 'signaling'
  /** Signaling is back and the media session is being rebuilt. */
  | 'media'
  /** The media session failed and is being retried. */
  | 'media-failed'
  /** The media session moved to another tab or window; nothing is retried. */
  | 'elsewhere'

/** Why the meeting is not fully connected, or null when it is. */
export function reconnectReason(s: ConnectionInputs): ReconnectReason | null {
  if (!s.online) return 'offline'
  if (!s.channelJoined) return 'signaling'
  if (!s.sfuReady) return s.sfuTakenOver ? 'elsewhere' : s.sfuError ? 'media-failed' : 'media'
  return null
}

/**
 * How long trouble must last before the banner shows. A one-second blip
 * recovers on its own, and a banner that flashed for each would be noise; a
 * real outage outlasts this.
 */
export const SHOW_AFTER_MS = 1_500

/** Once shown, the banner stays at least this long, so it never just flickers. */
export const MIN_VISIBLE_MS = 1_000

export type ReconnectBannerTimer = {
  /** The current reason (null = fully connected). Call on every change. */
  update: (reason: ReconnectReason | null) => void
  dispose: () => void
}

/**
 * Turns the reason over time into what should be on screen: shown only once
 * trouble has lasted SHOW_AFTER_MS, then kept at least MIN_VISIBLE_MS, and
 * relabelled at once (no delay) when the reason changes while it is up.
 */
export function createReconnectBannerTimer(opts: {
  onChange: (visible: ReconnectReason | null) => void
  showAfterMs?: number
  minVisibleMs?: number
  now?: () => number
  setTimeoutFn?: (fn: () => void, ms: number) => unknown
  clearTimeoutFn?: (handle: unknown) => void
}): ReconnectBannerTimer {
  const showAfterMs = opts.showAfterMs ?? SHOW_AFTER_MS
  const minVisibleMs = opts.minVisibleMs ?? MIN_VISIBLE_MS
  // Monotonic, like the timers themselves: a wall-clock change must not cut the
  // minimum time short or stretch it.
  const now = opts.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()))
  const setT = opts.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms))
  const clearT = opts.clearTimeoutFn ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>))

  let latest: ReconnectReason | null = null
  let shown: ReconnectReason | null = null
  let shownAt = 0
  let showTimer: unknown = null
  let hideTimer: unknown = null
  let disposed = false

  const cancel = (h: unknown) => { if (h !== null) clearT(h) }

  const show = (reason: ReconnectReason) => {
    shown = reason
    shownAt = now()
    opts.onChange(reason)
  }

  const hide = () => {
    shown = null
    opts.onChange(null)
  }

  const update = (reason: ReconnectReason | null) => {
    if (disposed) return
    latest = reason
    if (reason !== null) {
      cancel(hideTimer)
      hideTimer = null
      if (shown !== null) {
        if (shown !== reason) {
          // Already up: say what is happening now, without another wait.
          shown = reason
          opts.onChange(reason)
        }
        return
      }
      if (showTimer === null) {
        showTimer = setT(() => {
          showTimer = null
          if (!disposed && latest !== null) show(latest)
        }, showAfterMs)
      }
      return
    }
    cancel(showTimer)
    showTimer = null
    if (shown === null) return
    const left = minVisibleMs - (now() - shownAt)
    if (left <= 0) {
      hide()
      return
    }
    if (hideTimer === null) {
      hideTimer = setT(() => {
        hideTimer = null
        if (!disposed && latest === null && shown !== null) hide()
      }, left)
    }
  }

  const dispose = () => {
    disposed = true
    cancel(showTimer)
    cancel(hideTimer)
    showTimer = null
    hideTimer = null
  }

  return { update, dispose }
}
